import { forwardRef } from 'react';
import type { InputHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { cx } from './cx.ts';

/**
 * Text input / textarea with the Dispatch Deck field chrome: a recessed
 * `well` box with a hairline border that warms on hover and switches to
 * the signal accent on focus. Replaces the legacy bottom-border-only
 * fields (which read as empty voids on dark surfaces).
 */
const FIELD_CLASSES =
  'w-full rounded border border-line bg-well text-paper placeholder:text-paper-faint transition-colors duration-120 hover:border-line-strong focus:border-signal-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-signal/20 disabled:pointer-events-none disabled:opacity-60';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  readonly mono?: boolean;
  readonly invalid?: boolean;
  /** 32px field in `text-dense` with tighter padding, for dense rows. */
  readonly compact?: boolean;
  /**
   * Width constraint for the field, applied to a wrapper rather than to the
   * input itself. `FIELD_CLASSES` carries `w-full`, and a `w-*` passed through
   * `className` has identical specificity — which of them wins is decided by
   * Tailwind's stylesheet order, not by the call site. Callers that need a
   * bounded width (a dense table cell, say) constrain the box instead.
   */
  readonly wrapperClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { mono = false, invalid = false, compact = false, className, wrapperClassName, ...rest },
  ref,
): JSX.Element {
  const field = (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cx(
        FIELD_CLASSES,
        compact ? 'h-8 px-2 text-dense max-md:text-field-touch' : 'h-9 px-3 text-body max-md:h-10 max-md:text-field-touch',
        mono && 'font-mono',
        invalid && 'border-bad-line',
        className,
      )}
      {...rest}
    />
  );
  return wrapperClassName === undefined ? field : <span className={cx('block', wrapperClassName)}>{field}</span>;
});

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  readonly mono?: boolean;
  readonly invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { mono = false, invalid = false, className, rows, ...rest },
  ref,
): JSX.Element {
  return (
    <textarea
      ref={ref}
      rows={rows ?? 4}
      aria-invalid={invalid || undefined}
      className={cx(FIELD_CLASSES, 'px-3 py-2 text-body max-md:text-field-touch', mono && 'font-mono', invalid && 'border-bad-line', className)}
      {...rest}
    />
  );
});
